/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import * as Switch from "@radix-ui/react-switch";
import {
  CSSObject,
  Menu,
  menuClasses,
  MenuItem,
  MenuItemStyles,
  Sidebar,
  sidebarClasses,
} from "react-pro-sidebar";
import Avatar from "@mui/material/Avatar";
import AssessmentTwoToneIcon from "@mui/icons-material/AssessmentTwoTone";
import MovieTwoToneIcon from "@mui/icons-material/MovieTwoTone";
import AutoAwesomeTwoToneIcon from "@mui/icons-material/AutoAwesomeTwoTone";
import BusinessTwoToneIcon from "@mui/icons-material/BusinessTwoTone";
import SupportTwoToneIcon from "@mui/icons-material/SupportTwoTone";
import LightModeTwoToneIcon from "@mui/icons-material/LightModeTwoTone";
import NightsStayTwoToneIcon from "@mui/icons-material/NightsStayTwoTone";
import { useNavigate } from "react-router-dom";
import {
  checkPermission,
  PermissionsProvider,
  usePermissions,
} from "../components/PermissionsContext";
import { NotificationProvider, UserProvider, useUser } from "../components/UserContext";
import { AD_UPLOAD_SETTLED_EVENT, UploadOptionBar, UploadToast } from "../components/UploadToast";
import { enterpriseAPI, RTA } from "..";
import { sessionInfo } from "../utils/utils";
import { NotifyOptionBar, NotifyToast } from "../components/NotifyToast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faLock } from "@fortawesome/free-solid-svg-icons";
import localforage from "localforage";
import "./Root.css";
import { checkIfCollaboratorIsAuthenticated } from "../auth/auth";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useResolvedMedia } from "../hooks/useResolvedMedia";
import {
  negotiationSidebarRules,
  negotiationPaymentReady,
  paymentReadinessRuleLabel,
  type PaymentReadinessProblem,
} from "./negotiationReadiness";
import { readHallSettlementMode } from "../services/hallSettlementMode";
import { NEGOTIATION_BALANCE_READINESS_CHANGED_EVENT } from "../services/negotiationBalance";
import { PIX_AUTOMATICO_READINESS_CHANGED_EVENT } from "../services/pixAutomatico";
import { PendingBidRecoveryPanel } from "../components/PendingBidRecovery";

type NavTitleProps = {
  title: string;
  icon: React.ReactNode;
  isMobile?: boolean;
  onHamburgerClick?: () => void;
};

type SidebarProps = {
  activateItem: string;
  onUploadClick?: () => void;
  onNotificationClick?: () => void;
  unreadNotifications?: number;
  openNotifications?: boolean;
  drawerOpen?: boolean;
  onDrawerClose?: () => void;
  isTablet?: boolean;
};

type RootProps = {
  nav?: NavTitleProps;
  sidebar?: SidebarProps;
  page?: React.ReactNode;
};

const sidebarCSS: CSSObject = {
  [`.${sidebarClasses.container}`]: {
    display: "flex",
    backgroundColor: "var(--neutral-2)",
    height: "100%",
  },
  [`.${menuClasses.root}`]: {
    height: "100%",
  },
};

const menuItemCSS: MenuItemStyles = {
  button: (params) => {
    if (params.level > 0) return;
    if (params.active) {
      return {
        borderLeft: "3px solid var(--brand-11)",
        color: "var(--brand-11)",
        "&:hover": {
          background: "var(--brand-a2)",
        },
      };
    }

    return {
      color: "var(--brand-12)",
      "&:hover": {
        background: "var(--brand-a2)",
      },
    };
  },
};

const THEME_STORAGE_KEY = "dakasa-theme";

function applyTheme(dark: boolean) {
  const root = document.documentElement;
  root.classList.toggle("dark-theme", dark);
  // Legacy "dark" class — strip it so only "dark-theme" is the source of truth.
  root.classList.remove("dark");
}

function readInitialDarkPreference(): boolean {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "dark") return true;
  if (stored === "light") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

const SidebarItem: React.FC<{
  id: string;
  icon: React.ReactNode;
  label: string;
  active: boolean;
  rules?: { [key: string]: boolean };
  onClick: () => void;
}> = (props) => {
  const rulesEntries = Object.entries(props.rules || { "": true });
  const allRulesPassed = rulesEntries.every(([_, value]) => value === true);

  const ItemContent: React.FC = () => {
    if (allRulesPassed) {
      return <>{props.label}</>;
    }

    return (
      <div className="sidebarItemRestrictedContainer">
        <div className="sidebarItemRestricted">
          <span>{props.label}</span>
          <FontAwesomeIcon icon={faLock} className="sidebarItemLockIcon" />
        </div>
        <div className="sidebarItemRestrictedTooltip">
          <div className="sidebarItemRestrictedTooltipRuleArrow" />
          {rulesEntries.map(([rule, value], index) => (
            <div key={index} className="sidebarItemRestrictedTooltipRule">
              <div
                className={`sidebarItemRestrictedTooltipRuleIndicator ${value ? "passed" : "failed"}`}
              />
              <span>{rule}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <MenuItem
      id={props.id}
      icon={props.icon}
      onClick={props.onClick}
      active={props.active}
      disabled={!allRulesPassed}
      className={!allRulesPassed ? "sidebarItemRestrictedMenuItem" : ""}
    >
      <ItemContent />
    </MenuItem>
  );
};

function SideBar(props: SidebarProps) {
  const logoWhite = `${window.location.origin}/assets/Logo_DAKASA_V1_Branco.png`;
  const logoBlack = `${window.location.origin}/assets/Logo_DAKASA_V1_Preto.png`;
  const { permissions } = usePermissions();
  const { concludedTutorials, avatar, amaryCourse } = useUser();
  // The collaborator avatar can arrive as a raw compressor manifest (.json).
  // Resolve it so the sidebar mini avatar renders the real image (or MUI's
  // person icon when empty) instead of a broken manifest reference.
  const resolvedAvatar = useResolvedMedia(avatar || "");
  const navigate = useNavigate();
  const [hallSettlementMode, setHallSettlementMode] = React.useState<"prepaid" | "legacy">(
    "legacy",
  );
  const [negotiationBalanceReady, setNegotiationBalanceReady] = React.useState(false);
  const [hallModeAvailable, setHallModeAvailable] = React.useState(false);
  const readinessSequence = React.useRef(0);
  const [hasPaymentMethodActive, setHasPaymentMethodActive] = React.useState<boolean>(false);
  // Weekly payment and prepaid Hall account readiness stay separate.
  // Enterprise projects both; radio and TV never use the balance readiness.
  const [paymentReadinessProblem, setPaymentReadinessProblem] =
    React.useState<PaymentReadinessProblem>(null);
  const [hasOrgAvatar, setHasOrgAvatar] = React.useState<boolean>(false);
  const [hasActiveHallAd, setHasActiveHallAd] = React.useState<boolean>(false);
  const [orgOnboardingComplete, setOrgOnboardingComplete] = React.useState<boolean>(false);
  const [isDark, setIsDark] = React.useState<boolean>(readInitialDarkPreference);
  const ThemeIcon = isDark ? <NightsStayTwoToneIcon /> : <LightModeTwoToneIcon />;

  class SocketEvents {
    static paymentMethodConfigured() {
      // The legacy event payload carries card state only. Treat the event as a
      // refresh hint and read the organization-wide projection instead.
      void checkNegotiationReadiness();
    }

    static orgAvatarUpdated(data: { status?: string; manifest?: string }) {
      if (data?.status !== "success") return;
      // The org logo just finished processing → unlock the gated items live and
      // keep the loader's localforage flag in sync (mirrors the payment path).
      localforage.setItem("orgHasAvatar", true);
      setHasOrgAvatar(true);
    }
  }

  function checkActiveItem(item: string) {
    return props.activateItem.toLowerCase() === item.toLowerCase();
  }

  async function checkIfHasOrgAvatar() {
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) return false;

    const headers: { [key: string]: string | undefined } = {
      "X-JWT": JWT,
    };

    try {
      const response = await enterpriseAPI.get("/has-org-avatar", { headers });
      const has = response.data?.hasOrgAvatar === true;
      localforage.setItem("orgHasAvatar", has);
      setHasOrgAvatar(has);
    } catch (error) {
      console.error("Error checking org avatar:", error);
      return false;
    }
  }

  async function checkNegotiationReadiness() {
    const sequence = ++readinessSequence.current;
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) return false;
    try {
      const response = await enterpriseAPI.get<{
        orgOnboardingComplete: boolean;
        hasOrgAvatar: boolean;
        paymentMethodReady: boolean;
        hallSettlementMode?: "prepaid" | "legacy";
        negotiationBalanceReady?: boolean;
        activeHallAdCount: number;
      }>("/negotiation-readiness", { headers: { "X-JWT": JWT } });
      if (sequence !== readinessSequence.current) return false;
      let currentMode: "prepaid" | "legacy" | undefined;
      try {
        currentMode = await readHallSettlementMode(JWT, response.data);
      } catch {
        currentMode = undefined;
      }
      if (sequence !== readinessSequence.current) return false;
      setHallModeAvailable(currentMode !== undefined);
      setOrgOnboardingComplete(response.data.orgOnboardingComplete);
      setHasOrgAvatar(response.data.hasOrgAvatar);
      setHasPaymentMethodActive(response.data.paymentMethodReady);
      setHallSettlementMode(currentMode ?? response.data.hallSettlementMode ?? "legacy");
      setNegotiationBalanceReady(response.data.negotiationBalanceReady === true);
      setPaymentReadinessProblem(null);
      setHasActiveHallAd(response.data.activeHallAdCount > 0);
      await Promise.all([
        localforage.setItem("orgHasAvatar", response.data.hasOrgAvatar),
        localforage.setItem("hasPaymentMethodActive", response.data.paymentMethodReady),
        localforage.setItem("hasActiveHallAd", response.data.activeHallAdCount > 0),
      ]);
      return true;
    } catch (error) {
      console.error("Error checking negotiation readiness:", error);
      // An unknown answer is named as such and never keeps a previous
      // "configured" value (the loaders show an error page for it).
      if (sequence !== readinessSequence.current) return false;
      setHallModeAvailable(false);
      setHasPaymentMethodActive(false);
      setNegotiationBalanceReady(false);
      setPaymentReadinessProblem("unavailable");
      return false;
    }
  }

  React.useEffect(() => {
    applyTheme(isDark);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, isDark ? "dark" : "light");
    } catch {
      // localStorage may be unavailable in private mode; ignore.
    }
  }, [isDark]);

  React.useEffect(() => {
    checkIfHasOrgAvatar();
    checkNegotiationReadiness();
    // Hydrate instantly from cache so the item isn't briefly shown as locked
    // while /has-org-avatar is in flight.
    localforage.getItem("orgHasAvatar").then((cached) => {
      if (cached === true) setHasOrgAvatar(true);
    });
    localforage.getItem("hasActiveHallAd").then((cached) => {
      if (cached === true) setHasActiveHallAd(true);
    });

    let readinessRefreshTimer: number | undefined;
    const onUploadSettled = (event: Event) => {
      const detail = (event as CustomEvent<{ feature?: string }>).detail;
      if (detail?.feature !== "hall") return;
      readinessRefreshTimer = window.setTimeout(() => void checkNegotiationReadiness(), 1500);
    };
    window.addEventListener(AD_UPLOAD_SETTLED_EVENT, onUploadSettled);
    const onPixReadinessChanged = () => void checkNegotiationReadiness();
    window.addEventListener(PIX_AUTOMATICO_READINESS_CHANGED_EVENT, onPixReadinessChanged);
    window.addEventListener(NEGOTIATION_BALANCE_READINESS_CHANGED_EVENT, onPixReadinessChanged);
    RTA.on("negotiateMarket", onPixReadinessChanged);
    sessionInfo().then((info) => {
      if (!info) return;
      RTA.on(`paymentMethodConfigured-${info.org_id}`, SocketEvents.paymentMethodConfigured);
      // Org-scoped room (no -<iss> suffix): enterprise-rta emits the org-avatar
      // event to enterpriseRTAOrgAvatarUpdate-<orgId> so every collaborator of
      // the org unlocks the gated items live when the logo finishes processing.
      RTA.on(`enterpriseRTAOrgAvatarUpdate-${info.org_id}`, SocketEvents.orgAvatarUpdated);
    });

    return () => {
      window.removeEventListener(AD_UPLOAD_SETTLED_EVENT, onUploadSettled);
      window.removeEventListener(PIX_AUTOMATICO_READINESS_CHANGED_EVENT, onPixReadinessChanged);
      window.removeEventListener(
        NEGOTIATION_BALANCE_READINESS_CHANGED_EVENT,
        onPixReadinessChanged,
      );
      if (readinessRefreshTimer !== undefined) window.clearTimeout(readinessRefreshTimer);
      sessionInfo().then((info) => {
        if (!info) return;
        RTA.off(`paymentMethodConfigured-${info.org_id}`, SocketEvents.paymentMethodConfigured);
        RTA.off(`enterpriseRTAOrgAvatarUpdate-${info.org_id}`, SocketEvents.orgAvatarUpdated);
      });
    };
  }, []);

  const paymentRuleLabel = paymentReadinessRuleLabel(paymentReadinessProblem);
  const permissionViewAds =
    checkPermission(permissions.roomRadioAds, "view") ||
    checkPermission(permissions.roomTvAds, "view") ||
    checkPermission(permissions.hallAds, "view");
  const permissionViewOrg =
    checkPermission(permissions.orgInfos, "view") ||
    checkPermission(permissions.orgCollaborators, "view") ||
    checkPermission(permissions.orgRoles, "view") ||
    checkPermission(permissions.orgPayments, "view");
  return (
    <Sidebar
      rootStyles={sidebarCSS}
      breakPoint="md"
      toggled={props.drawerOpen}
      collapsed={props.isTablet}
      onBackdropClick={props.onDrawerClose}
      width="225px"
      collapsedWidth="64px"
    >
      <Menu menuItemStyles={menuItemCSS}>
        <div className="sidebarMenu">
          <div>
            <img src={isDark ? logoWhite : logoBlack} className="logoSidebar" alt="DaKasa" />
            <MenuItem
              id="rootNavbarPainel"
              icon={<AssessmentTwoToneIcon />}
              onClick={() => navigate("/")}
              active={checkActiveItem("painel")}
            >
              Painel
            </MenuItem>
            <SidebarItem
              id="rootNavbarAds"
              icon={<MovieTwoToneIcon />}
              onClick={() => navigate("/anuncios")}
              active={checkActiveItem("ads")}
              label="Anúncios"
              rules={
                !amaryCourse
                  ? {
                      "Logo da organização configurado": hasOrgAvatar,
                      [hallSettlementMode === "prepaid" && !paymentReadinessProblem
                        ? "Saldo do Átrio ou pagamento semanal configurado"
                        : paymentRuleLabel]:
                        hasPaymentMethodActive ||
                        negotiationPaymentReady({
                          paymentMethodReady: false,
                          hallSettlementMode,
                          negotiationBalanceReady,
                          hallModeAvailable,
                        }),
                      "Permissão para visualizar essa página": permissionViewAds,
                    }
                  : undefined
              }
            />
            <SidebarItem
              id="rootNavbarNegotiate"
              icon={<AutoAwesomeTwoToneIcon />}
              onClick={() => navigate("/negociar")}
              active={checkActiveItem("negociar")}
              label="Negociar"
              rules={
                !amaryCourse
                  ? negotiationSidebarRules(
                      {
                        orgOnboardingComplete,
                        hasOrgAvatar,
                        paymentMethodReady: hasPaymentMethodActive,
                        hallSettlementMode,
                        negotiationBalanceReady,
                        hallModeAvailable,
                        hasActiveHallAd,
                      },
                      checkPermission(permissions.bid, "view"),
                      concludedTutorials.includes("negotiate"),
                      paymentRuleLabel,
                    )
                  : undefined
              }
            />
            <SidebarItem
              id="rootNavbarOrg"
              icon={<BusinessTwoToneIcon />}
              onClick={() => navigate("/organizacao")}
              active={checkActiveItem("organizacao")}
              label="Organização"
              rules={
                !amaryCourse
                  ? {
                      "Permissão para visualizar essa página": permissionViewOrg,
                    }
                  : undefined
              }
            />
          </div>
          <div>
            <NotifyOptionBar
              onClick={props.onNotificationClick || (() => {})}
              isOpen={props.openNotifications || false}
            />
            <UploadOptionBar onClick={props.onUploadClick || (() => {})} />
            <MenuItem
              id="rootNavbarSupport"
              icon={<SupportTwoToneIcon />}
              onClick={() => navigate("/suporte")}
              active={checkActiveItem("suporte")}
            >
              Suporte
            </MenuItem>
            <MenuItem icon={ThemeIcon} onClick={() => setIsDark((d) => !d)}>
              <Switch.Root className="SwitchRoot" checked={isDark}>
                <Switch.Thumb className="SwitchThumb" />
              </Switch.Root>
            </MenuItem>
            <MenuItem
              id="rootNavbarConfig"
              onClick={() => navigate("/configuracoes")}
              active={checkActiveItem("configuracoes")}
              icon={
                <Avatar
                  className="sidebarAvatar"
                  src={resolvedAvatar || undefined}
                  sx={{
                    bgcolor: checkActiveItem("configuracoes")
                      ? "var(--brand-11)"
                      : "var(--brand-12)",
                    color: "var(--neutral-2)",
                  }}
                />
              }
            >
              Configurações
            </MenuItem>
          </div>
        </div>
      </Menu>
    </Sidebar>
  );
}

function NavTitle(props: NavTitleProps) {
  return (
    <div className="navTitle">
      {props.isMobile && (
        <button
          className="navHamburger"
          onClick={props.onHamburgerClick}
          aria-label="Abrir menu"
          type="button"
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
      )}
      {props.icon}
      <h2>{props.title}</h2>
    </div>
  );
}

const RootContentPage: React.FC<{
  title?: string;
  icon?: React.ReactNode;
  page?: React.ReactNode;
  isMobile?: boolean;
  onHamburgerClick?: () => void;
}> = React.memo(
  (props) => {
    return (
      <div className="rootContent">
        {props.icon && props.title && (
          <NavTitle
            title={props.title}
            icon={props.icon}
            isMobile={props.isMobile}
            onHamburgerClick={props.onHamburgerClick}
          />
        )}
        {props.icon && props.title ? (
          <div className="pageContent">{props.page}</div>
        ) : (
          <>{props.page}</>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.title === nextProps.title &&
      prevProps.page === nextProps.page &&
      prevProps.isMobile === nextProps.isMobile
    );
  },
);

function Root(props: RootProps) {
  const [openUploadToast, setOpenUploadToast] = React.useState(false);
  const [isOpenNotifications, setIsOpenNotifications] = React.useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  class SocketEvent {
    static openUploadToast() {
      setOpenUploadToast(true);
    }

    static async resetPermissions() {
      await localforage.removeItem("TrustedSession");
      await checkIfCollaboratorIsAuthenticated();
    }
  }

  React.useEffect(() => {
    RTA.connect();
  }, []);

  React.useEffect(() => {
    const eventNames: string[] = [];

    sessionInfo().then((info) => {
      if (!info) return;
      const uploadEvent = `openUploadToast-${info.org_id}-${info.iss}`;
      const resetUserEvent = `resetPermissions-${info.org_id}-${info.iss}`;
      const resetOrgEvent = `resetPermissions-${info.org_id}`;

      eventNames.push(uploadEvent, resetUserEvent, resetOrgEvent);

      RTA.on(uploadEvent, SocketEvent.openUploadToast);
      RTA.on(resetUserEvent, SocketEvent.resetPermissions);
      RTA.on(resetOrgEvent, SocketEvent.resetPermissions);
    });

    return () => {
      if (eventNames.length) {
        RTA.off(eventNames[0], SocketEvent.openUploadToast);
        RTA.off(eventNames[1], SocketEvent.resetPermissions);
        RTA.off(eventNames[2], SocketEvent.resetPermissions);
      }
    };
  }, []);

  return (
    <UserProvider>
      <PermissionsProvider>
        <div className="rootTemplate" id="rootTemplate">
          {props.sidebar && (
            <NotificationProvider>
              <SideBar
                activateItem={props.sidebar.activateItem}
                onUploadClick={() => {
                  setOpenUploadToast(!openUploadToast);
                  if (!openUploadToast) setIsOpenNotifications(false);
                }}
                onNotificationClick={() => {
                  setIsOpenNotifications(!isOpenNotifications);
                  if (!isOpenNotifications) setOpenUploadToast(false);
                }}
                openNotifications={isOpenNotifications}
                drawerOpen={drawerOpen}
                onDrawerClose={() => setDrawerOpen(false)}
                isTablet={isTablet}
              />
              <NotifyToast
                isOpen={isOpenNotifications}
                onClose={() => setIsOpenNotifications(false)}
              />
            </NotificationProvider>
          )}
          <RootContentPage
            title={props.nav && props.nav.title}
            icon={props.nav && props.nav.icon}
            page={props.page}
            isMobile={isMobile}
            onHamburgerClick={() => setDrawerOpen(true)}
          />
        </div>
        <UploadToast
          isOpen={openUploadToast}
          onClose={() => setOpenUploadToast(false)}
          onNewUpload={() => setOpenUploadToast(true)}
        />
        <PendingBidRecoveryPanel />
      </PermissionsProvider>
    </UserProvider>
  );
}

export default Root;
